/**
 * instruction-progress.js
 *
 * Bundles two responsibilities so a single script tag covers both:
 *
 * 1. <instruction-resume-banner> — Renders a "resume where you left
 *    off" banner if the visitor has previously seen ≥ step 2 in this
 *    article (read from `sewsimple:progress:{handle}` in
 *    LocalStorage). Click resumes either by scrolling to the inline
 *    step or by dispatching `instruction-focus:open` to
 *    `<instruction-focus-view>`, depending on `data-irb-prefers-focus`.
 *
 * 2. ProgressTracker (singleton) — Auto-bookmarks the highest viewed
 *    step. Uses IntersectionObserver against `[data-si-step]` inside
 *    `.sewing-instructions`. Zero user interaction — completely passive.
 *
 * Storage key: `sewsimple:progress:{article.handle}`
 * Value shape: `{ step: number, total: number, updated: ISO-string }`
 */

import { toInt } from "./utils.js";

const PROGRESS_KEY_PREFIX = "sewsimple:progress:";
const VIEWED_THRESHOLD = 0.6; // step must be ≥ 60% visible
const VIEWED_DEBOUNCE_MS = 400;

const SELECTORS = {
  scope: ".sewing-instructions",
  step: "[data-si-step]",
};

// Storage shape:
//   sewsimple:progress:{handle}:{instructionId}
//     → { instructionId, step, total, updated }
//
// One article can hold multiple instructions; each gets its own entry,
// so a visitor sewing two related guides isn't fighting one shared
// bookmark.
function progressKey(handle, instructionId) {
  return `${PROGRESS_KEY_PREFIX}${handle}:${instructionId}`;
}

function readProgress(handle, instructionId) {
  try {
    const raw = localStorage.getItem(progressKey(handle, instructionId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (typeof parsed.step !== "number") return null;
    return parsed;
  } catch (_) {
    return null;
  }
}

function writeProgress(handle, instructionId, step, total) {
  try {
    const existing = readProgress(handle, instructionId);
    const highest = existing ? Math.max(existing.step, step) : step;
    if (existing && existing.step === highest && existing.total === total) return;
    localStorage.setItem(
      progressKey(handle, instructionId),
      JSON.stringify({
        instructionId,
        step: highest,
        total,
        updated: new Date().toISOString(),
      }),
    );
    document.dispatchEvent(
      new CustomEvent("instruction-progress:updated", {
        detail: { handle, instructionId, step: highest, total },
      }),
    );
  } catch (_) {
    /* LocalStorage unavailable — non-fatal */
  }
}

function readAllProgress(handle) {
  const prefix = `${PROGRESS_KEY_PREFIX}${handle}:`;
  const out = [];
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key || !key.startsWith(prefix)) continue;
      const raw = localStorage.getItem(key);
      if (!raw) continue;
      try {
        const parsed = JSON.parse(raw);
        if (typeof parsed.step === "number") out.push(parsed);
      } catch (_) {
        /* ignore corrupt entry */
      }
    }
  } catch (_) {
    return [];
  }
  return out;
}

function clearAllProgress(handle) {
  const prefix = `${PROGRESS_KEY_PREFIX}${handle}:`;
  try {
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(prefix)) keysToRemove.push(key);
    }
    keysToRemove.forEach((k) => localStorage.removeItem(k));
  } catch (_) {
    /* ignore */
  }
}

// -- ProgressTracker singleton -----------------------------------

class ProgressTracker {
  #observer = null;
  #scope = null;
  #handle = "";
  #pending = new Map(); // composite key "instructionId:step" → timeout id

  attach(scope) {
    if (this.#scope === scope) return;
    this.#scope = scope;
    this.#handle = scope.dataset.siHandle || "";
    if (!this.#handle) return;

    if (!("IntersectionObserver" in window)) return;

    this.#observer = new IntersectionObserver(
      (entries) => this.#onIntersect(entries),
      { threshold: [VIEWED_THRESHOLD] },
    );
    scope.querySelectorAll(SELECTORS.step).forEach((el) => this.#observer.observe(el));
  }

  #onIntersect(entries) {
    entries.forEach((entry) => {
      if (!entry.isIntersecting || entry.intersectionRatio < VIEWED_THRESHOLD) return;

      const instructionId = toInt(entry.target.dataset.siInstructionId, 0);
      const localStep = toInt(entry.target.dataset.siStepLocal, 0);
      const localTotal = toInt(entry.target.dataset.siStepTotalLocal, 0);
      if (instructionId === 0 || localStep === 0) return;

      const key = `${instructionId}:${localStep}`;
      if (this.#pending.has(key)) return;
      // Debounce: only count the step as "viewed" if it stays visible
      // for VIEWED_DEBOUNCE_MS. Avoids logging every step the visitor
      // scrolls past on the way down.
      const timer = setTimeout(() => {
        this.#pending.delete(key);
        writeProgress(this.#handle, instructionId, localStep, localTotal);
      }, VIEWED_DEBOUNCE_MS);
      this.#pending.set(key, timer);
    });
  }
}

const tracker = new ProgressTracker();

function autoAttachTracker() {
  document.querySelectorAll(SELECTORS.scope).forEach((scope) => tracker.attach(scope));
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", autoAttachTracker, { once: true });
} else {
  autoAttachTracker();
}

// -- <instruction-resume-banner> ----------------------------------

class InstructionResumeBanner extends HTMLElement {
  #step = 0;
  #total = 0;
  #instructionId = 1;
  #instructionTitle = "";
  #instructionsMeta = [];

  constructor() {
    super();
    this.onResumeClick = this.onResumeClick.bind(this);
    this.onDismissClick = this.onDismissClick.bind(this);
    this.onProgressUpdated = this.onProgressUpdated.bind(this);
  }

  connectedCallback() {
    try {
      this.init();
    } catch (error) {
      console.error("[InstructionResumeBanner:init]", error);
    }
  }

  init() {
    this.#getDataAttributes();
    if (!this.handle) return;

    this.#injectStyles();
    this.#refresh();

    // Keep the banner in sync while the visitor is reading: every time
    // a step gets viewed (scroll auto-track or focus-mode swipe), we
    // dispatch `instruction-progress:updated` and re-render. Without
    // this the banner would only reflect state at page load and stay
    // stale until the next visit.
    document.addEventListener("instruction-progress:updated", this.onProgressUpdated);
  }

  onProgressUpdated(event) {
    if (event?.detail?.handle && event.detail.handle !== this.handle) return;
    this.#refresh();
  }

  #refresh() {
    // Find the most recently updated progress entry across all
    // instructions on this article and surface that one.
    const all = readAllProgress(this.handle).filter((p) => p.step >= 2);
    if (all.length === 0) {
      this.setAttribute("hidden", "");
      this.innerHTML = "";
      return;
    }
    all.sort((a, b) => (b.updated || "").localeCompare(a.updated || ""));
    const latest = all[0];

    this.#instructionId = toInt(latest.instructionId, 1);
    this.#step = latest.step;
    const meta = this.#instructionsMeta.find((m) => m.id === this.#instructionId);
    this.#total = latest.total || (meta ? meta.total : 0);
    this.#instructionTitle = meta?.title || "";
    if (this.#total === 0) {
      this.setAttribute("hidden", "");
      return;
    }

    this.#render();
    this.removeAttribute("hidden");
  }

  #getDataAttributes() {
    this.handle = this.dataset.irbHandle || "";
    this.label = this.dataset.irbLabel || "Schritt";
    this.resumeLabel = this.dataset.irbResumeLabel || "Hier weitermachen";
    this.dismissLabel = this.dataset.irbDismissLabel || "Von vorn beginnen";
    this.messageSingle =
      this.dataset.irbMessageSingle || "Du warst zuletzt bei {{step}} {{index}}/{{total}}.";
    this.messageMulti =
      this.dataset.irbMessageMulti ||
      "In „{{instructionTitle}}“ warst du zuletzt bei {{step}} {{index}}/{{total}}.";
    this.prefersFocus = this.dataset.irbPrefersFocus === "true";

    try {
      this.#instructionsMeta = JSON.parse(this.dataset.irbInstructions || "[]");
    } catch (_) {
      this.#instructionsMeta = [];
    }
  }

  #injectStyles() {
    if (document.getElementById("irb-styles")) return;
    const style = document.createElement("style");
    style.id = "irb-styles";
    style.textContent = IRB_CSS;
    document.head.appendChild(style);
  }

  #render() {
    const hasMultipleInstructions = this.#instructionsMeta.length > 1;
    const template = hasMultipleInstructions && this.#instructionTitle
      ? this.messageMulti
      : this.messageSingle;
    const message = template
      .replace("{{step}}", this.label)
      .replace("{{index}}", String(this.#step))
      .replace("{{total}}", String(this.#total))
      .replace("{{instructionTitle}}", this.#instructionTitle);

    this.innerHTML = `
      <div class="irb-wrap" role="region" aria-live="polite">
        <div class="irb-content">
          <span class="irb-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M12 8v4l3 3"/>
              <circle cx="12" cy="12" r="9"/>
            </svg>
          </span>
          <p class="irb-message">${escapeHTML(message)}</p>
        </div>
        <div class="irb-actions">
          <button type="button" class="irb-btn irb-btn--primary" data-irb-resume>
            ${escapeHTML(this.resumeLabel)}
          </button>
          <button type="button" class="irb-btn irb-btn--ghost" data-irb-dismiss aria-label="${escapeAttr(this.dismissLabel)}">
            ${escapeHTML(this.dismissLabel)}
          </button>
        </div>
      </div>
    `;

    this.querySelector("[data-irb-resume]").addEventListener("click", this.onResumeClick);
    this.querySelector("[data-irb-dismiss]").addEventListener("click", this.onDismissClick);
  }

  onResumeClick() {
    if (this.prefersFocus && document.querySelector("instruction-focus-view")) {
      document.dispatchEvent(
        new CustomEvent("instruction-focus:open", {
          detail: { step: this.#step, instructionId: this.#instructionId },
        }),
      );
      return;
    }
    const target = document.getElementById(
      `anleitung-${this.#instructionId}-schritt-${this.#step}`,
    );
    if (target) {
      target.scrollIntoView({ behavior: "smooth", block: "start" });
      target.classList.add("is-resume-highlight");
      setTimeout(() => target.classList.remove("is-resume-highlight"), 1600);
    }
  }

  onDismissClick() {
    clearAllProgress(this.handle);
    this.setAttribute("hidden", "");
  }

  disconnectedCallback() {
    document.removeEventListener("instruction-progress:updated", this.onProgressUpdated);
  }
}

function escapeHTML(str) {
  return String(str)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

function escapeAttr(str) {
  return escapeHTML(str).replace(/"/g, "&quot;");
}

const IRB_CSS = `
instruction-resume-banner { display: block; margin-bottom: 1.25rem; }
instruction-resume-banner[hidden] { display: none !important; }

.irb-wrap {
  display: flex; flex-wrap: wrap; gap: 0.75rem;
  align-items: center; justify-content: space-between;
  padding: 0.85rem 1rem;
  border-radius: 14px;
  background: color-mix(in srgb, var(--si-accent, #c1573a) 18%, var(--color-bg, #fff));
  border: 1px solid color-mix(in srgb, var(--si-accent, #c1573a) 30%, transparent);
}
.irb-content {
  display: flex; gap: 0.6rem; align-items: center; min-width: 0; flex: 1 1 240px;
}
.irb-icon {
  flex-shrink: 0; display: inline-flex;
  width: 28px; height: 28px;
  align-items: center; justify-content: center;
  color: var(--si-accent, #c1573a);
}
.irb-icon svg { width: 22px; height: 22px; }
.irb-message {
  margin: 0;
  font-size: 0.95rem;
  line-height: 1.4;
}
.irb-actions {
  display: inline-flex; gap: 0.5rem; flex-wrap: wrap;
}
.irb-btn {
  border: 0; cursor: pointer;
  font-weight: 600; font-size: 0.88rem;
  padding: 0.5rem 0.95rem;
  border-radius: 999px;
  transition: transform 0.15s ease, background 0.15s ease;
}
.irb-btn--primary {
  background: var(--si-accent, #c1573a);
  color: var(--color-bg, #fff);
}
.irb-btn--primary:hover { transform: translateY(-1px); }
.irb-btn--ghost {
  background: transparent;
  color: currentColor;
  opacity: 0.7;
}
.irb-btn--ghost:hover { opacity: 1; }

.sewing-step.is-resume-highlight {
  animation: irb-highlight 1.6s ease;
}
@keyframes irb-highlight {
  0%   { box-shadow: 0 0 0 0 color-mix(in srgb, var(--si-accent, #c1573a) 35%, transparent); }
  40%  { box-shadow: 0 0 0 10px color-mix(in srgb, var(--si-accent, #c1573a) 18%, transparent); }
  100% { box-shadow: 0 0 0 0 color-mix(in srgb, var(--si-accent, #c1573a) 0%, transparent); }
}
`;

if (!customElements.get("instruction-resume-banner")) {
  customElements.define("instruction-resume-banner", InstructionResumeBanner);
}
